import { createSelector } from 'reselect';
import { spliceString, isAlphaNumeric } from '../../utils/helper';
import { ICSC_ORG } from '../../config';

const authDomain = (state) => state.auth;
const courseDomain = (state) => state.course;

const makeSelectAuthToken = () =>
  createSelector(
    authDomain,
    (subdomain) => (subdomain.user && subdomain.user.token) || null
  );
const makeSelectUser = () =>
  createSelector(authDomain, (subdomain) => subdomain.user || null);
const makeSelectAuthProceeding = () =>
  createSelector(authDomain, (subdomain) => subdomain.isProceeding);
const makeSelectAuthError = () =>
  createSelector(authDomain, (subdomain) => subdomain.error);
const makeSelectIsImpersonating = () =>
  createSelector(authDomain, (subdomain) => subdomain.isImpersonating);

const makeSelectUserId = () =>
  createSelector(
    authDomain,
    (subdomain) => (subdomain.user && subdomain.user._id) || null
  );
const makeSelectExpireDates = () =>
  createSelector(
    authDomain,
    (subdomain) => (subdomain.user && subdomain.user.expireDates) || []
  );
const makeSelectProgramExpireDate = (programId) =>
  createSelector(
    makeSelectExpireDates(),
    (expireDates) =>
      (Array.isArray(expireDates) &&
        expireDates.find(({ program }) => programId === program)) ||
      []
  );
const makeSelectUserProgram = () =>
  createSelector(makeSelectUser(), (user) => user && user.program);
const makeSelectUserMembershipInfo = () =>
  createSelector(makeSelectUser(), (user) => user && user.membershipInfo);
const makeSelectUserMembership = () =>
  createSelector(makeSelectUser(), (user) => user && user.membership);
const makeSelectIsIcscOrg = () =>
  createSelector(makeSelectUser(), (user) => user && user.org === ICSC_ORG);

const makeSelectCourses = () =>
  createSelector(courseDomain, (subdomain) => subdomain.courses);

const makeSelectCompletedLessons = () =>
  createSelector(courseDomain, (subdomain) => subdomain.completedLessons);

const makeSelectCoursesLoading = () =>
  createSelector(courseDomain, (subdomain) => subdomain.loading);

const makeSelectCurrentCourseId = () =>
  createSelector(courseDomain, (subdomain) => subdomain.currentCourse);

const makeSelectTerms = () =>
  createSelector(courseDomain, (subdomain) => subdomain.terms);

const makeSelectCurrentCourse = (courseId) =>
  createSelector(
    makeSelectCourses(),
    makeSelectCurrentCourseId(),
    makeSelectCompletedLessons(),
    (courses, id, completedLessons) => {
      const currentCourse =
        courses.find(({ id }) => id === courseId) || courses[id];

      return currentCourse
        ? {
            ...currentCourse,
            lessonAndQuizzes: currentCourse.lessonAndQuizzes.map((lesson) => ({
              ...lesson,
              completed: completedLessons.includes(lesson._id),
            })),
          }
        : Array.isArray(courses) && courses[0]
        ? {
            ...courses[0],
            lessonAndQuizzes: courses[0].lessonAndQuizzes.map((lesson) => ({
              ...lesson,
              completed: completedLessons.includes(lesson._id),
            })),
          }
        : undefined;
    }
  );

const makeSelectCurriculumIncompletedLessons = () =>
  createSelector(
    makeSelectCourses(),
    makeSelectCompletedLessons(),
    (courses, completedLessons) => {
      return courses
        .reduce(
          (prev, cur) =>
            prev.concat(
              cur.lessonAndQuizzes.filter(
                (lesson) => !completedLessons.includes(lesson._id)
              )
            ),
          []
        )
        .map(({ _id }) => _id);
    }
  );

const makeSelectCurriculumCompletePercent = () =>
  createSelector(
    makeSelectCourses(),
    makeSelectCurriculumIncompletedLessons(),
    (courses, incompletedLessons) => {
      const totalCourses = courses.length;
      const incompletedCourses = courses.filter((course) =>
        course.lessonAndQuizzes.some(({ _id }) =>
          incompletedLessons.includes(_id)
        )
      );
      return Math.round(
        (parseFloat(totalCourses - incompletedCourses.length) / totalCourses) *
          100
      );
    }
  );

const makeSelectCurrentLessonId = () =>
  createSelector(courseDomain, (subdomain) => subdomain.currentLesson);

const checkIfHeading = (description, id) => {
  const hStart = description.indexOf('<h', id);
  const hEnd = description.indexOf('</h', id);

  if (hEnd < hStart) return true;
  return false;
};

const makeSelectCurrentLesson = () =>
  createSelector(
    makeSelectCurrentCourse(),
    makeSelectCurrentLessonId(),
    makeSelectCompletedLessons(),
    makeSelectTerms(),
    (course, id, completedLessons, terms) => {
      if (course !== undefined) {
        const currentLesson = course.lessonAndQuizzes[id]
          ? course.lessonAndQuizzes[id]
          : course.lessonAndQuizzes[0];
        let description = currentLesson.description;

        terms.forEach(({ title, description: definition, link, id }) => {
          let idx = description.toLowerCase().indexOf(title.toLowerCase());

          //@todo: replace all terms?
          while (idx > -1) {
            if (
              (description[idx - 1] === '>' || description[idx - 1] === ' ') &&
              !isAlphaNumeric(description[idx + title.length]) &&
              !checkIfHeading(description, idx)
            ) {
              const substr = description.substr(idx, title.length);
              description = spliceString(
                description,
                idx,
                title.length,
                `<span class="term" data-id="${id}">${substr}
                <span class="tooltip">
                  ${definition}
                  ${
                    link
                      ? `<span style="color: rgb(50,31,219);" onclick="window.open('${link}', '_blank')">Read More</span>`
                      : ''
                  }
                </span>
              </span>`
              );

              break;
            } else {
              idx = description
                .toLowerCase()
                .indexOf(title.toLowerCase(), idx + 1);
            }
          }
        });

        return {
          ...currentLesson,
          description,
          completed:
            currentLesson && completedLessons.includes(currentLesson._id),
        };
      }

      return {};
    }
  );

const makeSelectCourseCompletePercent = () =>
  createSelector(makeSelectCurrentCourse(), (course) => {
    if (!course) {
      return 0;
    }
    return Math.round(
      (parseFloat(
        course.lessonAndQuizzes.reduce(
          (prev, cur) => prev + (cur.completed ? 1 : 0),
          0
        )
      ) /
        course.lessonAndQuizzes.length) *
        100
    );
  });

const makeSelectProgressChanged = () =>
  createSelector(courseDomain, (subdomain) => subdomain.progressChanged);

const makeSelectForum = () =>
  createSelector(courseDomain, (subdomain) => subdomain.posts);

export {
  makeSelectUser,
  makeSelectUserId,
  makeSelectAuthToken,
  makeSelectAuthProceeding,
  makeSelectAuthError,
  makeSelectIsImpersonating,
  makeSelectExpireDates,
  makeSelectProgramExpireDate,
  makeSelectUserProgram,
  makeSelectIsIcscOrg,
  makeSelectCourses,
  makeSelectCompletedLessons,
  makeSelectCurriculumIncompletedLessons,
  makeSelectCurriculumCompletePercent,
  makeSelectCurrentCourse,
  makeSelectCurrentCourseId,
  makeSelectCurrentLesson,
  makeSelectCurrentLessonId,
  makeSelectCourseCompletePercent,
  makeSelectCoursesLoading,
  makeSelectProgressChanged,
  makeSelectForum,
  makeSelectUserMembershipInfo,
  makeSelectUserMembership,
};
